import { safeAwait, SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';

import { TradeIdeaRestful } from './restful';
import {
  ingressFind,
  ingressIdea,
  ingressTradeIdeaComment,
  ingressTradeIdeaIndividual,
  ingressTrader,
} from './ingress';
import {
  PostTradeIdea,
  PostTradeIdeaComment,
  TradeIdea,
  TradeIdeaComment,
  TradeIdeaPostError,
  TradeIdeaResponse,
  Trader,
  TraderStatus,
  TraderJSON,
  ZingFlag,
  TradeIdeaRequestParams,
  PremiumIdeaRequestParams,
} from './entities';

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'trade_idea_find_error'
    | 'trade_idea_get_trade_idea_error'
    | 'trade_idea_get_traders_to_review_error'
    | 'trade_idea_review_trader_error'
    | 'trade_idea_post_trade_idea_error'
    | 'trade_idea_post_trade_idea_comment_error'
    | 'trade_idea_flag_error'
    | 'trade_idea_vote_error'
    | 'trade_idea_delete_trade_idea_error';
  type: 'error';
}

export type TradeIdeaRequestEvent = ErrorEvent;

export class TradeIdeaRequest extends ListenableSubscribable<TradeIdeaRequestEvent> {
  private restful: TradeIdeaRestful;

  constructor(session: Session) {
    super();
    this.restful = new TradeIdeaRestful(session);
  }

  public find = async (params: TradeIdeaRequestParams): SafePromise<TradeIdeaResponse> => {
    const find = ingressFind(await this.restful.find(params));
    if (find.err) {
      this.dispatch({
        error: find.err,
        errorType: 'trade_idea_find_error',
        type: 'error',
      });
    }
    return find;
  };

  public getPremiumIdeas = async (params: PremiumIdeaRequestParams, expertProductId: string): SafePromise<any> => {
    const premiumIdeas = await this.restful.getPremiumIdeas(params, expertProductId);
    return premiumIdeas;
  };

  public getMorePremiumIdeas = async (params: PremiumIdeaRequestParams, expertProductId: string): SafePromise<any> => {
    const premiumIdeas = await this.restful.getMorePremiumIdeas(params, expertProductId);
    return premiumIdeas;
  };

  public getAllPremiumIdeas = async (params: PremiumIdeaRequestParams): SafePromise<any> => {
    const premiumIdeas = await this.restful.getAllPremiumIdeas(params);
    return premiumIdeas;
  };

  public getExpertsArray = async () => {
    const expertArray = await this.restful.getExpertsArray();
    return expertArray;
  };

  public getPackagesArray = async () => {
    const packagesArray = await this.restful.getPackagesArray();
    return packagesArray;
  };

  public getTradeIdea = async (tradeIdeaId: string): SafePromise<TradeIdea> => {
    const tradeIdea = ingressTradeIdeaIndividual(await this.restful.getTradeIdea(tradeIdeaId));
    if (tradeIdea.err) {
      this.dispatch({
        error: tradeIdea.err,
        errorType: 'trade_idea_get_trade_idea_error',
        type: 'error',
      });
    }
    return tradeIdea;
  };

  public getTradersToReview = async (): SafePromise<Trader[]> => {
    const tradeIdea = await this.restful.getTradersToReview();
    if (tradeIdea.err) {
      if (tradeIdea.err) {
        this.dispatch({
          error: tradeIdea.err,
          errorType: 'trade_idea_get_traders_to_review_error',
          type: 'error',
        });
      }
      return tradeIdea as unknown as SafeType<Trader[]>;
    } else {
      return { ok: tradeIdea.ok.data.map((trader: TraderJSON) => ingressTrader(trader)) };
    }
  };

  public reviewTrader = async (trader: Trader, status: TraderStatus): SafePromise<any> => {
    const reviewer = await this.restful.reviewTrader(trader, status);
    if (reviewer.err) {
      this.dispatch({
        error: reviewer.err,
        errorType: 'trade_idea_review_trader_error',
        type: 'error',
      });
    }
    return reviewer;
  };
  public postTradeIdea = async (data: PostTradeIdea): SafePromise<TradeIdea, string, TradeIdeaPostError | Response> => {
    const response = await this.restful.postTradeIdea(data);
    if (response.err) {
      const errorResponse = await safeAwait((response.err.data as Response).json());
      if (errorResponse.err) {
        this.dispatch({
          error: errorResponse.err,
          errorType: 'trade_idea_post_trade_idea_error',
          type: 'error',
        });
        return errorResponse as unknown as SafeType<TradeIdea, string, TradeIdeaPostError | Response>;
      } else {
        const error = new SafeError('trade_idea_invalid_input', 'trade idea invalid input', errorResponse.ok);
        this.dispatch({
          error,
          errorType: 'trade_idea_post_trade_idea_error',
          type: 'error',
        });
        return { err: error } as unknown as SafeType<TradeIdea, string, TradeIdeaPostError | Response>;
      }
    } else {
      return { ok: ingressIdea(response.ok.data) };
    }
  };

  public postTradeIdeaComment = async (data: PostTradeIdeaComment): Promise<TradeIdeaComment | any> => {
    const response = await this.restful.postTradeIdeaComment(data);

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'trade_idea_post_trade_idea_comment_error',
        type: 'error',
      });
      return response;
    } else {
      if (response.ok.status === 'failed') return response;
      return ingressTradeIdeaComment(response.ok.data);
    }
  };

  public flag = async (trade_idea_id: number, type: string, reason: string | null = null): SafePromise<ZingFlag> => {
    const zingFlag = await this.restful.flag(trade_idea_id, type, reason);
    if (zingFlag.err) {
      this.dispatch({
        error: zingFlag.err,
        errorType: 'trade_idea_flag_error',
        type: 'error',
      });
    }
    return zingFlag;
  };

  public vote = async (tradeIdea: TradeIdea, type: 'upvote' | 'downvote'): Promise<any> => {
    const vote = await this.restful.vote(tradeIdea, type);
    if (vote.err) {
      this.dispatch({
        error: vote.err,
        errorType: 'trade_idea_vote_error',
        type: 'error',
      });
    }
    return vote;
  };

  public deleteTradeIdea = async (trade_idea_id: number) => {
    const deleteTradeIdea = await this.restful.deleteTradeIdea(trade_idea_id);
    if (deleteTradeIdea.err) {
      this.dispatch({
        error: deleteTradeIdea.err,
        errorType: 'trade_idea_delete_trade_idea_error',
        type: 'error',
      });
    }
    return deleteTradeIdea;
  };
}
